import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import TextInput from '../../elements/TextInput';
import MaterialIcon from "../../elements/MaterialIcon.jsx";
import { changeCalendarViewMode, checkOffRow, saveGroceryList } from "../../../redux/actions/calendar.js";
import { selectMealIdea } from "../../../redux/actions/mealIdeas.js";
import { calendarPageViews } from "../../../redux/reducers/calendar.js";
import { slugify } from "../../../utilities/slugify.js";
import GroceryListItemEditDropdown from "./GroceryListItemEditDropdown.jsx";
import { ideasOf } from "./mealIdeasOfItem.js";
import { selectMealIdeasIndex } from "../../../redux/food/selectors.js";
import Modal from "../Modal.jsx";
import GroceryItemEditor from "../groceryItems/GroceryItemEditor.jsx";
import ItemTags from "../groceryItems/ItemTags.jsx";

/**
 * A row of the active list. `groceryList` and `groceryListItem` are the list's draft and the row's
 * model in it (food/groceryListDraft.js): typing changes the model, and a blur saves the draft.
 * Its amount shows after its name and its notes under it; both are set in the item editor.
 *
 * Ticking works offline: it is an op, kept until it is sent. Typing a new row does not yet, since the
 * whole-list save is online-only, so the blank new row is disabled while the browser is offline, and
 * so is deleting a row, which saves the list too.
 */
export default function GroceryListItemEdit({
	                                            groceryList,
	                                            groceryListItemEditCallback,
	                                            groceryListItem,
	                                            onKeyDown,
	                                            dragHandleProps,
	                                            showCheckboxes,
	                                            readOnly = false,
                                            }) {
	const dispatch = useDispatch();
	const mealIdeas = useSelector((state) => ideasOf(groceryListItem, selectMealIdeasIndex(state).ideasById), shallowEqual);
	const offline = useSelector((state) => !!state.dashboard && state.dashboard.isOnline === false);

	/** Open an idea this item came from on the Meal Ideas tab. */
	const openMealIdea = (event, mealIdea) => {
		event.preventDefault();
		dispatch(selectMealIdea(mealIdea.id));
		dispatch(changeCalendarViewMode(slugify(calendarPageViews.MEAL_IDEAS)));
	};

	const initiateGroceryListSaving = () => {
		return dispatch(saveGroceryList(groceryList));
	};

	const onBlur = () => {
		initiateGroceryListSaving();
	};

	const deleteItem = () => {
		initiateGroceryListSaving();
	};

	const groceryListItemChanged = ({ name, value, checked, saveGroceryListUpdate = false }) => {
		if(name === 'checked_off') {
			/**
			 * Ticked straight away, online or not, and sent when it can be; it snaps back if the server
			 * refuses it. It stays where it is on the active list for a day, struck through, and
			 * unticking puts it back there.
			 */
			dispatch(checkOffRow(groceryListItem.id, !!checked));
			if(groceryListItemEditCallback) {
				groceryListItemEditCallback();
			}
			return;
		}
		if(name === 'grocery_list_item_name') {
			name = 'name';
		}

		groceryListItem[name] = value;
		if(saveGroceryListUpdate){
			initiateGroceryListSaving();
		}
		if(groceryListItemEditCallback) {
			groceryListItemEditCallback();
		}
	};

	const groceryListItemEdit = (event) => {
		return groceryListItemChanged(event.target);
	};
	onKeyDown = onKeyDown.bind(null, groceryListItem);
	const inputRef = useRef(null);
	groceryListItem.inputRef = inputRef;
	const hasId = !!groceryListItem.id;


	// The element the item editor was opened from, which it grows out of; null while it is closed.
	const [editorOrigin, setEditorOrigin] = useState(null);
	const closeEditor = () => setEditorOrigin(null);

	const checkedOff = hasId && !!groceryListItem.checked_off;
	const removeFromList = () => {
		closeEditor();
		groceryListItemChanged({ name: 'name', value: '', saveGroceryListUpdate: true });
	};
	// Just ticked, so when it was last bought is no longer news.
	const showLastBought = !readOnly && !checkedOff && !!groceryListItem.last_bought;
	const rowClasses = ['grocery_list_item', checkedOff && 'checked_off', !hasId && 'new'].filter(Boolean).join(' ');
	return (
		<div className={rowClasses}>
			<span className='grocery_list_item__handle' title={hasId ? 'Drag to reorder' : undefined}>
				{hasId &&
				<MaterialIcon {...dragHandleProps}>drag_indicator</MaterialIcon>
				}
			</span>
			{hasId && showCheckboxes &&
			<label className='grocery_list_item__check'>
				<input
					type='checkbox'
					className='filled-in'
					onChange={groceryListItemEdit}
					name='checked_off'
					checked={!!groceryListItem.checked_off} />
				<span></span>
			</label>
			}
			{!hasId &&
			<MaterialIcon className='grocery_list_item__add unclickable'>add</MaterialIcon>
			}
			<div className='grocery_list_item__text'>
				{hasId &&
				<button
					type='button'
					className='btn-flat grocery_list_item_name grocery_list_item__open'
					title={`About ${groceryListItem.name}`}
					onClick={(event) => setEditorOrigin(event.currentTarget)}
				>
					{groceryListItem.name}
					{!!groceryListItem.quantity &&
					<span className='grocery_list_item__quantity'>{groceryListItem.quantity}</span>
					}
				</button>
				}
				{!hasId &&
				<div className='input-field'>
					<TextInput
						ref={inputRef}
						autoComplete='off'
						type='text'
						name='grocery_list_item_name'
						value={groceryListItem.name}
						onChange={groceryListItemEdit}
						onBlur={onBlur}
						onKeyDown={onKeyDown}
						disabled={offline}
						placeholder={offline ? 'Adding items needs a connection' : 'Add an item'}
						error={groceryListItem.hasDuplicate ? 'Is Duplicated' : undefined}
					/>
				</div>
				}
				{hasId && !!groceryListItem.notes &&
				<div className='grocery_list_item__notes'>{groceryListItem.notes}</div>
				}
				{(mealIdeas.length > 0 || showLastBought || (groceryListItem.tags || []).length > 0) &&
				<div className='grocery_list_item__hints'>
					{mealIdeas.map((mealIdea) =>
					<a key={mealIdea.id} href='#' className='grocery_list_item_meal_idea' onClick={(event) => openMealIdea(event, mealIdea)}>{mealIdea.name}</a>
					)}
					{showLastBought &&
					<span className='grocery_list_item_last_bought'>Last bought {groceryListItem.last_bought.format('LL')}</span>
					}
					<ItemTags tags={groceryListItem.tags} />
				</div>
				}
			</div>
			<span className='grocery_list_item__menu'>
				{hasId && !checkedOff &&
				<GroceryListItemEditDropdown
					groceryListItem={groceryListItem}
					onChange={groceryListItemChanged}
					offline={offline}
				/>
				}
			</span>
			{/* On the body: the row is a draggable, and a drag's transform would carry a fixed modal with it. */}
			{!!editorOrigin && ReactDOM.createPortal(
				<Modal className='meal_idea_modal grocery_item_modal' origin={editorOrigin} onClose={closeEditor}>
					<GroceryItemEditor
						row={groceryListItem}
						onClose={closeEditor}
						onRemove={checkedOff || readOnly ? null : removeFromList}
					/>
				</Modal>,
				document.body
			)}
		</div>
	);
}